import { useCallback } from "react";

import Box from "ds/components/Box";
import ProgressBar from "ds/components/ProgressBar";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import IconEmphasized from "ds/components/IconEmphasized";
import TileWrapper from "ds/components/Tile/Wrapper";
import TileContent from "ds/components/Tile/Content";
import TileTitle from "ds/components/Tile/Title";
import LearnLimitedAccessBanner from "components/LearnLimitedAccessBanner";
import { ChevronRight, Clock, TickCircleFilled } from "components/icons/generated";
import useUserGuideGroups from "shared/UserGuides/useUserGuideGroups";
import Tooltip from "ds/components/Tooltip";

import useAssistantPanelLearnStore from "../../useStore";
import styles from "./styles.module.css";
import AssistantPanelLearnGroupsViewSkeleton from "./Skeleton";

const AssistantPanelLearnGroupsView = () => {
  const { setNavigationState } = useAssistantPanelLearnStore();
  const {
    userGuideGroups,
    userGuidesCompletedCount,
    userGuidesCount,
    overallTimeToComplete,
    loading,
  } = useUserGuideGroups();

  const handleGroupClick = useCallback(
    (groupId: string) => {
      setNavigationState({ level: "chapters", groupId });
    },
    [setNavigationState]
  );

  if (loading && !userGuideGroups) {
    return <AssistantPanelLearnGroupsViewSkeleton />;
  }

  return (
    <Box direction="column" padding="x-large" gap="large" grow="1" scrollable>
      <Box direction="column" gap="x-large">
        <Box direction="column" gap="medium" align="center">
          <Typography tag="h2" variant="p-t6" align="center">
            Learn Spacelift
          </Typography>
          <Typography tag="p" variant="p-body3" align="center">
            Start with the fundamentals — how stacks, runs, and policies work together to help you
            ship infrastructure safely. More tracks coming soon.
          </Typography>
        </Box>
        <LearnLimitedAccessBanner storageKey="assistant-panel-learn-access-notice" />
        <Box
          direction="row"
          align="center"
          gap="medium"
          fullWidth
          justify="between"
          padding="xx-large 0 0 0"
        >
          <Box direction="row" align="center" gap="small">
            <Icon src={Clock} color="secondary" />
            <Typography color="secondary" variant="p-body3" tag="span">
              {overallTimeToComplete} left
            </Typography>
          </Box>
          <ProgressBar
            current={userGuidesCompletedCount}
            max={userGuidesCount}
            isPercentage
            ariaLabel="Learning progress"
          />
        </Box>
      </Box>

      <Box direction="column" gap="medium" grow="1">
        {userGuideGroups?.map((group) => (
          <TileWrapper
            className={styles.tile}
            fullWidth
            onClick={() => handleGroupClick(group.id)}
            grow="0"
            key={group.id}
            data-userflow-id={`learn-mission-${group.id}`}
          >
            <TileContent gap="small">
              <Box align="center" fullWidth justify="between">
                <Box align="center" gap="medium">
                  <IconEmphasized icon={group.icon} color={group.color} />
                  <Box direction="column" gap="small">
                    <TileTitle variant="p-t7">{group.name}</TileTitle>
                    <Box direction="row" align="center" gap="small">
                      <Icon
                        src={
                          group.guidesCompletedCount === group.guidesCount
                            ? TickCircleFilled
                            : Clock
                        }
                        color="secondary"
                      />
                      <Tooltip
                        active={group.guidesCompletedCount !== group.guidesCount}
                        on={(props) => (
                          <Typography {...props} color="secondary" variant="p-body3" tag="span">
                            {group.guidesCompletedCount === group.guidesCount
                              ? "Complete"
                              : group.timeToComplete}
                          </Typography>
                        )}
                      >
                        Estimated time to complete: {group.timeToComplete}
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
                <Icon src={ChevronRight} color="secondary" />
              </Box>
            </TileContent>
          </TileWrapper>
        ))}
      </Box>
    </Box>
  );
};

export default AssistantPanelLearnGroupsView;
